import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../App";
import {
  LayoutDashboard,
  Ticket,
  BarChart3,
  SmilePlus,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Headset,
  BookOpen,
  DollarSign,
  MessageCircle,
  Sparkles,
  Webhook,
  GraduationCap,
  UserPlus,
  Gauge
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { ScrollArea } from "../components/ui/scroll-area";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/tickets", label: "Open Tickets", icon: Ticket },
  { path: "/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/satisfaction", label: "CSAT & NPS", icon: SmilePlus },
  { path: "/knowledge-base", label: "KB Gaps", icon: BookOpen },
  { path: "/kb-generator", label: "KB Generator", icon: Sparkles },
  { path: "/incomplete-payments", label: "Incomplete Payments", icon: DollarSign },
  { path: "/onboarding", label: "Customer Onboarding", icon: UserPlus },
  { path: "/ai-assistant", label: "AI Assistant", icon: MessageCircle },
  { path: "/webhook-monitor", label: "Auto-Responder", icon: Webhook },
  { path: "/chatbot-metrics", label: "Chatbot Metrics", icon: Gauge, adminOnly: true },
  { path: "/ai-training", label: "AI Training", icon: GraduationCap },
  { path: "/team", label: "Team", icon: Users },
  { path: "/settings", label: "Settings", icon: Settings },
];

const Layout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:transform-none ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-full flex flex-col border-r border-slate-200 bg-white">
          {/* Logo */}
          <div className="p-6 flex items-center justify-between">
            <Link to="/dashboard" className="flex flex-col">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#425B76] to-[#2D4156] shadow-lg shadow-[#425B76]/30 flex items-center justify-center">
                  <Headset className="w-5 h-5 text-white" />
                </div>
                <span className="font-bold text-lg text-slate-900">Beakon</span>
              </div>
              <span className="text-[10px] text-slate-400 mt-1 ml-[52px]">Powered by TalentDesk</span>
            </Link>
            <button
              className="lg:hidden text-slate-400 hover:text-slate-600"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3">
            <nav className="space-y-1">
              {navItems.filter((item) => !item.adminOnly || user?.role === "admin").map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? "bg-[#425B76]/10 text-[#425B76] font-medium"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User */}
          <div className="p-4 border-t border-slate-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <Avatar className="w-9 h-9">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-[#425B76]/10 text-[#425B76] text-sm font-medium">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                    <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border-slate-200">
                <DropdownMenuItem className="text-slate-500">
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-100" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden text-slate-400 hover:text-slate-600"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <h1 className="text-xl font-bold text-slate-900">{title}</h1>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
